<template>
  <div class="catalog-view">
    <!-- Database selector -->
    <div class="database-selector">
      <label>
        <input 
          type="checkbox" 
          v-model="removeMissingImages" 
          @change="onRemoveMissingImagesToggled" 
        />
        Hide Items Without Images
      </label>
    </div>

    <CategoryNav :tree="store.getCategoryTree()" @navigate="onCategoryNav"/>

    <!-- Catalog component (child) -->
    <Catalog 
      :primaryFilterKeys="store.getPrimaryFilterKeys()"
      :topLevelFilterOptions="store.getTopLevelFilterOptions()"
      :allAttributeOptions="store.getAllAttributeOptions()"
      :taxonomyFilterOptions="store.getTaxonomyFilterOptions()"
      :products="store.getCatalogProducts()"
      :totalProducts="store.getCatalogTotal()"
      :isLoading="store.getIsLoading()"
      :selectedFilters="store.getCatalogFilters().selectedFilters"
      :selectedCategoryPath="store.getCatalogFilters().selectedCategoryPath"
      :selectedAttrId="store.getCatalogFilters().selectedAttrId"
      :selectedAttrValue="store.getCatalogFilters().selectedAttrValue"
      :brandSearch="store.getCatalogFilters().brandSearch"
      :alliumSearch="store.getCatalogFilters().alliumSearch"
      :webCategorySearch="store.getCatalogFilters().webCategorySearch"
      :currentPage="store.getCatalogFilters().currentPage"
      :pageSize="store.getCatalogFilters().pageSize"
      @filters-changed="onCatalogFiltersChanged"
      @open-product="openProductPage"
      :showAttributeFlags="store.getCatalogFilters().showAttributeFlags"
    />
  </div>
</template>

<script>
import { ref, watch, onMounted, toRaw } from 'vue';
import CategoryNav from '../components/CategoryNav.vue';
import Catalog from '../components/Catalog.vue';
import { useProductStore } from '../store/productStore.js';
import router from '../plugins/router';

export default {
  name: 'CatalogView',
  components: {
    Catalog,
    CategoryNav
  },
  setup() {
    const store = useProductStore();

    // This local ref tracks which DB is currently selected in the dropdown
    const selectedDb = ref(store.state.selectedDatabase);

    // Local refs that mirror your watchers
    const internalFilters = ref({});
    const removeMissingImages = ref(false);

    // On mount, initialize store data once
    onMounted(async () => {
      // Initialize catalog data which will fetch products with current filters
      await store.initCatalogData();

      // Optionally initialize our internalFilters from the store
      internalFilters.value = JSON.parse(JSON.stringify(store.getCatalogFilters().selectedFilters));
    });

    /**
     * Watch internalFilters deeply.
     * Whenever internalFilters changes, update store's filters.
     */
    // watch(
    //   internalFilters,
    //   (newVal, oldValue) => {        
    //     if(Object.keys(toRaw(oldValue)).length > 0) {
    //       store.updateCatalogFilters({
    //       selectedFilters: JSON.parse(JSON.stringify(newVal)),
    //       removeMissingImages: removeMissingImages.value,
    //       showAttributeFlags: store.getCatalogFilters().showAttributeFlags,
    //       currentPage: 1
    //     });
    //     }
    //   },
    //   { deep: true }
    // );

    /**
     * Handle category navigation from CategoryNav component
     */
    watch(selectedDb, (newDb, oldDb) => {
      if (newDb && newDb !== oldDb) {
        console.log(`Database changed from ${oldDb} to ${newDb}. Reloading all data.`);
        store.setSelectedDatabase(newDb);
      }
    });

     function onCategoryNav(pathArr) {
      // 1. Get the complete current filter state from the store
      const currentFilters = store.getCatalogFilters();

      // 2. Update the store by merging the new category filter
      //    with the existing filters.
      store.updateCatalogFilters({
        ...currentFilters, // ✅ Spread all existing filters
        selectedFilters: {
          ...currentFilters.selectedFilters, // ✅ Spread existing selected filters (like brandId)
          webCategory: [ pathArr[pathArr.length - 1] ] // THEN, add/overwrite the webCategory
        },
        // Also update the category path for breadcrumbs
        selectedCategoryPath: pathArr, 
        currentPage: 1
      });
    }

    /**
     * If user changes the DB in the dropdown, re-fetch using the new DB
     */
    function onDbChange() {
      store.setSelectedDatabase(selectedDb.value);
    }

    function onRemoveMissingImagesToggled() {
      store.updateCatalogFilters({
        ...store.getCatalogFilters(),
        removeMissingImages: removeMissingImages.value,
        currentPage: 1
      });
    }

    /**
     * Called when Catalog child emits "filters-changed"
     */
    function onCatalogFiltersChanged(newFilters) {
      console.log("PARENT => onCatalogFiltersChanged => newFilters:", JSON.stringify(newFilters));
      store.updateCatalogFilters(newFilters);
    }

    /**
     * Open product detail route
     */
    function openProductPage({ productId }) {
      router.push(`/product/${productId}`);
    }

    return {
      store,
      selectedDb,
      internalFilters,
      removeMissingImages,

      onCategoryNav,
      onDbChange,
      onCatalogFiltersChanged,
      onRemoveMissingImagesToggled,
      openProductPage
    };
  }
};
</script>

<style scoped>
.catalog-view {
  width: 100%;
}

/* DB selector styling */
.database-selector {
  background: #fff;
  margin: 0 0 20px 20px;
  padding: 8px 12px;
  border-radius: 6px;
  display: inline-block;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}
.database-selector label {
  margin-right: 8px;
  font-weight: 600;
}
.database-selector select {
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
}
</style>
